<template>
  <AdminLayout>
    <template #breadcrumb>
      <breadcrumb-link
        view="AdminReimbursementGroupList"
        label="Reimbursement Groups"
        :active="true"
      />
    </template>

    <div class="box" style="position: relative">
      <b-loading :is-full-page="false" v-model="loading"></b-loading>
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <div>
              <h1 class="title mb-2">Manage Reimbursement Groups</h1>
              <p class="subtitle is-6 has-text-grey mt-0">Total: {{ totalCount }} groups</p>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <b-button type="is-success" @click="openCreateModal">
              <b-icon icon="plus"></b-icon>
              <span>Create New</span>
            </b-button>
          </div>
        </div>
      </div>
      <hr />

      <!-- Search -->
      <b-field>
        <div class="field-body">
          <b-field label="Search groups">
            <b-input
              v-model="search"
              placeholder="Search by number..."
              icon="magnify"
              @input="debouncedSearch"
            ></b-input>
          </b-field>
        </div>
      </b-field>

      <div class="table-container">
        <b-table
          :data="groups"
          :loading="loading"
          hoverable
          striped
          :paginated="true"
          backend-pagination
          :total="totalCount"
          :per-page="perPage"
          :current-page="currentPage"
          @page-change="onPageChange"
          pagination-position="bottom"
          default-sort="number"
          default-sort-direction="asc"
        >
          <b-table-column field="number" label="Group Number" v-slot="props" sortable>
            <span class="has-text-weight-semibold">{{ props.row.number }}</span>
          </b-table-column>

          <b-table-column label="Actions" width="160" v-slot="props">
            <div class="field is-grouped">
              <div class="control">
                <b-button
                  type="is-primary is-outlined"
                  size="is-small"
                  icon-left="pencil"
                  @click="openEditModal(props.row)"
                >
                  Edit
                </b-button>
              </div>
              <div class="control">
                <b-button
                  type="is-danger"
                  size="is-small"
                  icon-left="delete"
                  @click="confirmDelete(props.row)"
                >
                  Delete
                </b-button>
              </div>
            </div>
          </b-table-column>

          <template #empty>
            <div class="has-text-centered">
              <b-icon icon="emoticon-sad" size="is-large"></b-icon>
              <p>No groups found.</p>
            </div>
          </template>
        </b-table>
      </div>

      <!-- Create Modal -->
      <b-modal v-model="isCreateModalActive" :width="500" scroll="keep">
        <form @submit.prevent="createGroup">
          <div class="modal-card">
            <header class="modal-card-head">
              <p class="modal-card-title">Create New Group</p>
            </header>
            <section class="modal-card-body">
              <b-loading :is-full-page="false" v-model="isCreating"></b-loading>

              <b-field label="Group Number" required>
                <b-input
                  v-model="createForm.number"
                  placeholder="Enter group number"
                  required
                  maxlength="255"
                ></b-input>
              </b-field>
            </section>
            <footer class="modal-card-foot">
              <b-button @click="closeCreateModal">Cancel</b-button>
              <b-button type="is-success" native-type="submit" :loading="isCreating">
                Create
              </b-button>
            </footer>
          </div>
        </form>
      </b-modal>

      <!-- Edit Modal -->
      <b-modal v-model="showEditModal" :width="500" scroll="keep">
        <form @submit.prevent="handleUpdateGroup">
          <div class="modal-card" style="width: auto">
            <header class="modal-card-head">
              <p class="modal-card-title">Edit Group</p>
              <button type="button" class="delete" @click="showEditModal = false"></button>
            </header>
            <section class="modal-card-body">
              <b-loading :is-full-page="false" v-model="editLoading"></b-loading>

              <b-field
                label="Group Number"
                :type="{ 'is-danger': !!errors.number }"
                :message="errors.number"
              >
                <b-input
                  v-model="editForm.number"
                  placeholder="Enter group number"
                  required
                  maxlength="255"
                ></b-input>
              </b-field>
            </section>
            <footer class="modal-card-foot">
              <b-button @click="showEditModal = false">Cancel</b-button>
              <b-button type="is-primary" native-type="submit" :loading="editLoading">
                Save Changes
              </b-button>
            </footer>
          </div>
        </form>
      </b-modal>
    </div>
  </AdminLayout>
</template>

<script>
import request from '../../request';
import AdminLayout from '../../layouts/AdminLayout.vue';

export default {
  name: 'AdminReimbursementGroupList',
  components: {
    AdminLayout,
  },
  data() {
    return {
      loading: false,
      groups: [],
      totalCount: 0,
      currentPage: 1,
      perPage: 50,
      search: '',
      searchTimeout: null,
      isCreateModalActive: false,
      createForm: {
        number: '',
      },
      isCreating: false,
      showEditModal: false,
      editLoading: false,
      editingGroup: null,
      editForm: {},
      errors: {},
    };
  },
  created() {
    this.loadGroups();
  },
  methods: {
    async loadGroups() {
      this.loading = true;
      try {
        const params = new URLSearchParams();
        if (this.perPage) params.append('limit', this.perPage);
        if (this.currentPage > 1) params.append('page', this.currentPage);
        if (this.search.trim()) params.append('number', this.search.trim());

        const response = await request.get(`/admin/reimbursements/groups?${params.toString()}`);
        this.groups = response.data.groups || [];
        this.totalCount = parseInt(response.headers['x-total-count'] || '0');
      } catch (error) {
        console.error('Error loading groups:', error);
        this.$buefy.toast.open({
          message: 'Failed to load groups',
          type: 'is-danger',
        });
      } finally {
        this.loading = false;
      }
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadGroups();
    },

    debouncedSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadGroups();
      }, 300);
    },

    openCreateModal() {
      this.createForm = {
        number: '',
      };
      this.isCreateModalActive = true;
    },

    closeCreateModal() {
      this.isCreateModalActive = false;
      this.createForm = {
        number: '',
      };
    },

    async createGroup() {
      this.isCreating = true;
      try {
        await request.post('/admin/reimbursements/groups', this.createForm);

        this.$buefy.toast.open({
          message: 'Group created successfully',
          type: 'is-success',
        });

        this.closeCreateModal();
        this.loadGroups();
      } catch (error) {
        console.error('Error creating group:', error);
        this.$buefy.toast.open({
          message: error.response?.data?.message || 'Failed to create group',
          type: 'is-danger',
        });
      } finally {
        this.isCreating = false;
      }
    },

    openEditModal(group) {
      this.editingGroup = group;
      this.editForm = {
        number: group.number,
      };
      this.errors = {};
      this.showEditModal = true;
    },

    async handleUpdateGroup() {
      this.editLoading = true;
      this.errors = {};

      try {
        const payload = {
          number: this.editForm.number?.trim(),
        };

        await request.put(`/admin/reimbursements/groups/${this.editingGroup.id}`, payload);

        this.$buefy.toast.open({
          message: 'Group updated successfully',
          type: 'is-success',
        });

        this.showEditModal = false;
        this.loadGroups();
      } catch (error) {
        console.error('Error updating group:', error);
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else if (error.response?.data?.message) {
          this.$buefy.toast.open({
            message: error.response.data.message,
            type: 'is-danger',
          });
        } else {
          this.$buefy.toast.open({
            message: 'Failed to update group',
            type: 'is-danger',
          });
        }
      } finally {
        this.editLoading = false;
      }
    },

    confirmDelete(group) {
      this.$buefy.dialog.confirm({
        title: 'Delete Group',
        message: `Are you sure you want to delete group "<strong>${group.number}</strong>"? This action cannot be undone.`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.deleteGroup(group),
      });
    },

    async deleteGroup(group) {
      try {
        await request.delete(`/admin/reimbursements/groups/${group.id}`);

        this.$buefy.toast.open({
          message: 'Group deleted successfully',
          type: 'is-success',
        });

        this.loadGroups();
      } catch (error) {
        console.error('Error deleting group:', error);
        let errorMessage = 'Failed to delete group';

        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.status === 400) {
          errorMessage = 'Cannot delete this group - it may still be in use by reimbursements';
        }

        // Show error message in a dialog for better visibility
        this.$buefy.dialog.alert({
          title: 'Delete Failed',
          message: errorMessage,
          type: 'is-danger',
          hasIcon: true,
          ariaRole: 'alertdialog',
          ariaModal: true,
        });
      }
    },
  },
};
</script>
